import firebase from "firebase/app";

import { Header, Icon } from "semantic-ui-react";

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { db } from "astrid-firebase";
import organizationTypes from "astrid-firestore/src/api/organizations/constants/organizationTypes";
import productionStatuses from "astrid-firestore/src/api/productions/constants/productionStatuses";
import updateProduction from "astrid-firestore/src/api/productions/updateProduction";
import getCollectionDataById from "astrid-firestore/src/utils/getCollectionDataById";

import Flex from "../../../../components/Flex/Flex";
import Button from "../../../ui/components/Buttons/Button";
import PlaceholderSegment from "../../../ui/components/PlaceholderSegment/PlaceholderSegment";

import AddArticleModal from "../../../articles/components/AddArticleModal/AddArticleModal";
import Article from "../../../articles/components/Article/Article";
import ArticlesDeliveryNeededMessage from "../../../articles/components/ArticlesDeliveryNeededMessage/ArticlesDeliveryNeededMessage";
import useHasRight from "../../../authorization/hooks/useHasRight";
import useHasRole from "../../../authorization/hooks/useHasRole";
import { useOrganizationType } from "../../../organizations/state/organizationType";
import { getOrganizationPath } from "../../../routing/utils/getOrganizationPath";
import WorkSummary from "../../../works/components/WorkSummary/WorkSummary";

import ProductionOfferFormButton from "../../components/ProductionOfferForm/ProductionOfferFormButton";
import useProductionVoices from "../../hooks/useProductionVoices";

import ProductionOverviewDeliveryDateModal from "./components/ProductionOverviewDeliveryDateModal";
import ProductionOverviewInformationCard from "./components/ProductionOverviewInformationCard";

export default function ProductionOverviewView({
	work,
	publisher,
	production,
	articles,
	artifacts,
	distributionChannels,
	offers,
}) {
	const { t } = useTranslation();
	const organizationType = useOrganizationType();
	const navigate = useNavigate();
	const hasRight = useHasRight();
	const hasRole = useHasRole();

	const [addingArticle, setAddingArticle] = useState(false);
	const [openDeliveryDateModal, setOpenDeliveryDateModal] = useState(false);

	const voices = useProductionVoices({ production, offers });

	const canChangeDeliveryDate =
		hasRight("productions.settings.level4") ||
		[productionStatuses.DRAFT, productionStatuses.OFFER, productionStatuses.PLANNING].includes(production.status);

	return (
		<>
			{hasRight("productions.manage") && (
				<Flex gap={10} justifyContent="flex-start">
					<Button
						basic
						circular
						size="small"
						onClick={canChangeDeliveryDate ? () => setOpenDeliveryDateModal(true) : null}
						style={canChangeDeliveryDate ? undefined : { cursor: "default" }}
					>
						{t("deliveryDate", "Delivery date")}: {production.deliveryDate?.toDate().toLocaleDateString()}
						{canChangeDeliveryDate && (
							<Icon name="pencil" fitted style={{ paddingLeft: 7, fontSize: "0.8rem" }} />
						)}
					</Button>

					{hasRight("productions.finalize") && (
						<Button basic circular size="small" onClick={() => navigate("settings")}>
							{t("productionDueDate", "Due date")}:{" "}
							{production.productionDate?.toDate().toLocaleDateString()}
							<Icon name="pencil" fitted style={{ paddingLeft: 7, fontSize: "0.8rem" }} />
						</Button>
					)}

					{hasRight("articles.view") && (
						<Button
							basic
							circular
							size="small"
							onClick={() =>
								navigate(
									organizationType !== organizationTypes.PRODUCER
										? getOrganizationPath(`/works/${work.id}`)
										: `/admin/works/${work.id}`,
								)
							}
							style={{ marginLeft: "auto" }}
						>
							{t("goToWork", "Go to work")}
						</Button>
					)}
				</Flex>
			)}

			{hasRight("articles.view") && (
				<ArticlesDeliveryNeededMessage
					publisher={publisher}
					articles={articles}
					channels={distributionChannels}
				/>
			)}

			<Header as="h3">{t("metadata", "Metadata")}</Header>

			<WorkSummary work={work} publisher={publisher} overlay />

			{hasRight("productions.manage") && (
				<>
					<Header as="h3">{t("articles", "Articles")}</Header>

					{articles.map((article) => {
						return (
							<Article
								key={article.id}
								work={work}
								article={article}
								channels={distributionChannels}
								publisher={publisher}
								production={production}
								artifacts={artifacts?.filter((artifact) => artifact.article?.id === article.id)}
								overlay
							/>
						);
					})}
				</>
			)}

			{(hasRole("owner", "admin") || hasRight("articles.view")) &&
				![productionStatuses.PRODUCTION, productionStatuses.DONE].includes(production.status) && (
					<PlaceholderSegment onClick={() => setAddingArticle(true)}>
						<Button>{t("addArticle", "Add article")}</Button>
					</PlaceholderSegment>
				)}

			{addingArticle && (
				<AddArticleModal
					work={work}
					publisher={publisher}
					production={production}
					channels={distributionChannels}
					onSubmit={async ({ articleIds }) => {
						const articles = await getCollectionDataById(firebase, db.collection("articles"), articleIds);

						return updateProduction(firebase, { ref: production.ref }, { articles });
					}}
					onClose={() => setAddingArticle(false)}
				/>
			)}

			{production.status === productionStatuses.OFFER && hasRight("productions.accept") && (
				<ProductionOverviewInformationCard voices={voices} production={production} />
			)}

			{production.status === productionStatuses.OFFER && hasRight("productions.accept") && (
				<ProductionOfferFormButton
					primary
					production={production}
					afterSubmit={() => {
						return updateProduction(firebase, {
							ref: production.ref,
							status: productionStatuses.PLANNING,
						});
					}}
				/>
			)}

			{openDeliveryDateModal && (
				<ProductionOverviewDeliveryDateModal
					production={production}
					articles={articles}
					onClose={() => setOpenDeliveryDateModal(false)}
				/>
			)}
		</>
	);
}
